import { z } from "zod";

/**
 * MESAİ SAATLERİ — F-MSG-05
 *
 * ================================================================
 * BU SAATLER BİR KISIT DEĞİL, BİR BEKLENTİ AYARI
 * ================================================================
 * Şartnamenin kendi cümlesi: *"Dışında gönderilen mesaj **iletilir**
 * ama gönderene ... bilgisi gösterilir."*
 *
 * Yani mesai dışında yazmak ENGELLENMİYOR. Engellenseydi, gece
 * yarısı bir şey anlatmak isteyen öğrenci onu hiç yazamaz ve sabaha
 * unuturdu; oysa modülün var olma sebeplerinden biri tam da bu.
 * Engellenen tek şey **yanlış beklenti**: yazan kişi cevabın ne zaman
 * geleceğini biliyor.
 *
 * ================================================================
 * TANIMSIZ MESAİ = HİÇBİR ŞEY YAZMA
 * ================================================================
 * Varsayılan boş liste ve boşken ekranda hiçbir not GÖRÜNMEMELİ.
 * "09:00–18:00" gibi bir varsayılan uydurmak, koçun vermediği bir sözü
 * onun ağzından vermek olurdu — üstelik öğrenci o saatte cevap
 * bekleyip alamayınca kurumun sözü bozulmuş sayılırdı.
 */

/**
 * Haftanın günü — ISO: 1 Pazartesi … 7 Pazar.
 *
 * `Date.getDay()`in 0-Pazar düzeni DEĞİL: 0'ın hangi gün olduğu
 * okuyanın hafızasına kalıyor ve bu depoda gün numarası birkaç yerde
 * geçiyor. ISO numaralandırması yazıyla aynı sırayı takip ediyor.
 */
export const MESAI_GUNLERI = [
  [1, "Pazartesi"],
  [2, "Salı"],
  [3, "Çarşamba"],
  [4, "Perşembe"],
  [5, "Cuma"],
  [6, "Cumartesi"],
  [7, "Pazar"],
] as const;

/** `"HH:MM"` — 24 saat. */
const SAAT_BICIMI = /^([01]\d|2[0-3]):([0-5]\d)$/;

export const mesaiAraligiSemasi = z
  .object({
    day: z.number().int().min(1).max(7),
    start: z.string().regex(SAAT_BICIMI, "Saat 'SS:DD' biçiminde olmalı."),
    end: z.string().regex(SAAT_BICIMI, "Saat 'SS:DD' biçiminde olmalı."),
  })
  /*
    BİTİŞ BAŞLANGIÇTAN SONRA OLMALI. Eşit olması da geçersiz: sıfır
    uzunlukta bir aralık, ekranda "09:00–09:00 arası müsait" diye
    görünür ve hiçbir zaman doğru olmaz. Gece yarısını aşan vardiya
    (22:00–02:00) DESTEKLENMİYOR — koçluk mesaisi böyle çalışmıyor ve
    desteklemek, "hangi güne ait" sorusunu her okuyanın kafasında
    yeniden çözmesi demek olurdu.
  */
  .refine((a) => a.start < a.end, {
    message: "Bitiş saati başlangıçtan sonra olmalı.",
    path: ["end"],
  });

export type MesaiAraligi = z.infer<typeof mesaiAraligiSemasi>;

/** En çok kaç aralık — gün başına en fazla iki (öğle arası bölünmesi). */
export const MESAI_EN_COK_ARALIK = 14;

export const mesaiSemasi = z.array(mesaiAraligiSemasi).max(MESAI_EN_COK_ARALIK);

/**
 * Bilinmeyen bir JSON'u güvenle mesai listesine çevirir.
 *
 * Alan veritabanında `Json` ve elle de yazılmış olabilir; bozuk bir
 * kayıt yüzünden yazışma ekranının HİÇ açılmaması, mesai notunun
 * görünmemesinden çok daha kötü olurdu. Bozuk girdi boş liste sayılıyor.
 */
export function mesaiCoz(ham: unknown): MesaiAraligi[] {
  const sonuc = mesaiSemasi.safeParse(ham);
  return sonuc.success ? sonuc.data : [];
}

/** `"09:30"` → 570 (gün başından itibaren dakika). */
function dakikaya(saat: string): number {
  const [s, d] = saat.split(":");
  return Number(s) * 60 + Number(d);
}

/**
 * Verilen an mesai içinde mi.
 *
 * `gun` ve `dakika` ÇAĞIRAN TARAFTAN geliyor, `Date`ten değil: saat
 * dilimi çevrimi burada yapılsaydı bu modül `Intl`e bağlanır ve
 * "Europe/Istanbul" kuralı iki eve dağılırdı. Çevrimi sunucu yapıyor
 * (`zaman.ts` SAAT_DILIMI) ve buraya yalnızca sayı geliyor.
 */
export function mesaideMi(
  araliklar: readonly MesaiAraligi[],
  gun: number,
  dakika: number,
): boolean {
  return araliklar.some(
    (a) => a.day === gun && dakika >= dakikaya(a.start) && dakika < dakikaya(a.end),
  );
}

/**
 * Mesai saatlerinin okunabilir özeti — "Pazartesi–Cuma 09:00–18:00".
 *
 * ================================================================
 * AYNI SAATTEKİ ARDIŞIK GÜNLER BİRLEŞTİRİLİYOR
 * ================================================================
 * Beş satır "Pazartesi 09:00–18:00 · Salı 09:00–18:00 · …" yazmak,
 * cevabın ne zaman geleceğini soran kişiye okuması gereken bir tablo
 * vermek olurdu. Aradığı bilgi tek cümle.
 */
export function mesaiMetni(araliklar: readonly MesaiAraligi[]): string | null {
  if (araliklar.length === 0) return null;

  const gunAdi = new Map<number, string>(MESAI_GUNLERI.map(([n, a]) => [n, a]));
  const sirali = [...araliklar].sort((a, b) => a.day - b.day || a.start.localeCompare(b.start));

  const parcalar: string[] = [];
  let i = 0;
  while (i < sirali.length) {
    const bas = sirali[i]!;
    let son = i;
    // Ardışık gün + AYNI saat aralığı olduğu sürece birleştiriliyor.
    while (
      son + 1 < sirali.length &&
      sirali[son + 1]!.day === sirali[son]!.day + 1 &&
      sirali[son + 1]!.start === bas.start &&
      sirali[son + 1]!.end === bas.end
    ) {
      son += 1;
    }
    const gunler =
      son === i ? gunAdi.get(bas.day)! : `${gunAdi.get(bas.day)}–${gunAdi.get(sirali[son]!.day)}`;
    parcalar.push(`${gunler} ${bas.start}–${bas.end}`);
    i = son + 1;
  }
  return parcalar.join(" · ");
}
